/**
 * Step 11a — Local snapshot of the active trip.
 *
 * Read-only fallback that lets LTCC + ActiveLegPanel keep working when the
 * network drops. We store the *minimum* fields needed to render where the
 * user is in their trip (trip id/title/status, active_stop_index, ordered
 * stops with the centrala stop fields). No mutating actions are queued —
 * those stay disabled in offline mode and unblock when the user is online
 * again.
 *
 * Storage: localStorage. Keyed per-user so signing out / switching account
 * never surfaces another user's trip from disk. Also tagged with the active
 * trip id so swapping to another active trip rotates the snapshot.
 */

const STORAGE_KEY = "vantastika.activeTripSnapshot.v1";

export interface SnapshotStop {
  id: string;
  stop_order: number | null;
  stop_name: string | null;
  latitude: number | null;
  longitude: number | null;
  visited_at: string | null;
  stop_type?: string | null;
  address?: string | null;
  campings?: { name: string | null } | null;
}

export interface ActiveTripSnapshot {
  /** Schema version — bump when the shape changes so old snapshots get dropped. */
  version: 1;
  /** Owner of the snapshot. Prevents cross-account leakage on shared devices. */
  userId: string;
  /** Active trip id at the time of capture. */
  tripId: string;
  tripTitle: string | null;
  /** trips.travel_status — 'active' | 'paused' | 'completed' | 'planned' | ... */
  travelStatus: string | null;
  activeStopIndex: number | null;
  /** Ordered stop list with the centrala fields needed for LTCC. */
  stops: SnapshotStop[];
  /** ISO timestamp of when the snapshot was last captured from server data. */
  savedAt: string;
}

const isBrowser = (): boolean =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

/** Read the persisted snapshot for a given user. Returns null if none / mismatch. */
export const readActiveTripSnapshot = (
  userId: string | null | undefined,
): ActiveTripSnapshot | null => {
  if (!isBrowser() || !userId) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveTripSnapshot>;
    if (
      !parsed ||
      parsed.version !== 1 ||
      parsed.userId !== userId ||
      typeof parsed.tripId !== "string" ||
      !Array.isArray(parsed.stops)
    ) {
      return null;
    }
    return parsed as ActiveTripSnapshot;
  } catch {
    return null;
  }
};

/** Persist the snapshot. Best-effort — storage failures must not break the UI. */
export const writeActiveTripSnapshot = (snap: ActiveTripSnapshot): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch {
    // Quota / private mode etc — silently ignore. Snapshot is a nice-to-have.
  }
};

/** Drop the persisted snapshot (e.g. on logout or trip end). */
export const clearActiveTripSnapshot = (): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Build a snapshot from a server trip row. Returns null if the trip looks
 * unusable as a snapshot source (no id / no stops). Only safe centrala
 * fields are copied; everything else stays server-only.
 */
export const buildSnapshotFromTrip = (
  userId: string,
  trip: any,
): ActiveTripSnapshot | null => {
  if (!trip || typeof trip.id !== "string") return null;
  const stopsSrc: any[] = Array.isArray(trip.trip_stops) ? trip.trip_stops : [];
  const stops: SnapshotStop[] = [...stopsSrc]
    .sort((a, b) => (a?.stop_order ?? 0) - (b?.stop_order ?? 0))
    .map((s) => ({
      id: String(s.id),
      stop_order: typeof s.stop_order === "number" ? s.stop_order : null,
      stop_name: s.stop_name ?? null,
      latitude: typeof s.latitude === "number" ? s.latitude : null,
      longitude: typeof s.longitude === "number" ? s.longitude : null,
      visited_at: s.visited_at ?? null,
      stop_type: s.stop_type ?? null,
      address: s.address ?? null,
      campings: s.campings
        ? { name: s.campings.name ?? null }
        : null,
    }));

  return {
    version: 1,
    userId,
    tripId: trip.id,
    tripTitle: trip.title ?? null,
    travelStatus: trip.travel_status ?? null,
    activeStopIndex:
      typeof trip.active_stop_index === "number" ? trip.active_stop_index : null,
    stops,
    savedAt: new Date().toISOString(),
  };
};

/**
 * Inflate a snapshot back into a trip-like object that LTCC / ActiveLegPanel
 * can consume without code changes. The shape mirrors what `useTrips`
 * returns for the active trip (title, travel_status, active_stop_index,
 * trip_stops[]). Marked with `_fromSnapshot: true` so the UI can render
 * the offline banner / disable mutating actions.
 */
export const snapshotToTripLike = (snap: ActiveTripSnapshot): any => ({
  id: snap.tripId,
  title: snap.tripTitle,
  travel_status: snap.travelStatus,
  active_stop_index: snap.activeStopIndex,
  trip_stops: snap.stops,
  _fromSnapshot: true,
  _snapshotSavedAt: snap.savedAt,
});

/** Minutes since the snapshot was captured. Returns null if missing/invalid. */
export const snapshotAgeMinutes = (snap: ActiveTripSnapshot | null): number | null => {
  if (!snap?.savedAt) return null;
  const saved = Date.parse(snap.savedAt);
  if (!Number.isFinite(saved)) return null;
  return Math.max(0, Math.round((Date.now() - saved) / 60000));
};